import { createApi } from '@reduxjs/toolkit/query/react';

import { SessionExchangeResponse, ShareSession } from './sharedSession.api';
import { baseQueryWithAuth } from '../baseQuery';

export type SharedSessionGrant = Readonly<{
  id: string;
  share_session_id: string;
  grantee_user_id: string;
  access_level_id: string;
  granted_at: string;
  revoked_at: string | null;
  share_session?: ShareSession;
}>;

export type CreateSharedSessionGrantRequest = Readonly<{
  resource_type: string;
  grantee_user_email: string;
}>;

/**
 * API for shared session grants operations.
 */
export const sharedSessionGrantsApi = createApi({
  reducerPath: 'sharedSessionGrantsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SharedSessionGrant'],
  endpoints: (build) => ({
    /**
     * Create a new shared session grant.
     * POST /shared/grants
     * Body: { resource_type: string, grantee_user_email: string }
     * Returns: SharedSessionGrant
     */
    createSharedSessionGrant: build.mutation<SharedSessionGrant, CreateSharedSessionGrantRequest>({
      query: ({ resource_type, grantee_user_email }) => ({
        url: 'shared/grants',
        method: 'POST',
        body: { resource_type, grantee_user_email },
      }),
      invalidatesTags: ['SharedSessionGrant'],
    }),
    /**
     * Get all active shared session grants by grantee user ID.
     * GET /shared/grants/grantee/:granteeUserId
     * Returns: SharedSessionGrant[]
     */
    getSharedSessionGrantsByGranteeUserId: build.query<
      SharedSessionGrant[],
      { granteeUserId: string }
    >({
      query: ({ granteeUserId }) => `shared/grants/grantee/${granteeUserId}`,
      providesTags: (_result, _error, { granteeUserId }) => [
        { type: 'SharedSessionGrant', id: `grantee-${granteeUserId}` },
      ],
    }),
    /**
     * Get all active shared session grants by grantor user ID.
     * GET /shared/grants/grantor/:grantorUserId
     * Returns: SharedSessionGrant[]
     */
    getSharedSessionGrantsByGrantorUserId: build.query<
      SharedSessionGrant[],
      { grantorUserId: string }
    >({
      query: ({ grantorUserId }) => `shared/grants/grantor/${grantorUserId}`,
      providesTags: (_result, _error, { grantorUserId }) => [
        { type: 'SharedSessionGrant', id: `grantor-${grantorUserId}` },
      ],
    }),
    /**
     * Revoke an active shared session grant.
     * POST /shared/grants/:id/revoke
     * Returns: SharedSessionGrant
     */
    revokeSharedSessionGrant: build.mutation<SharedSessionGrant, { id: string }>({
      query: ({ id }) => ({
        url: `shared/grants/${id}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'SharedSessionGrant', id }],
    }),
    /**
     * Exchange a grant ID for a JWT access token.
     * POST /shared/exchange-session-by-grant
     * Body: { grant_id: string }
     * Returns: SessionExchangeResponse
     */
    exchangeSessionByGrant: build.mutation<SessionExchangeResponse, { grantId: string }>({
      query: ({ grantId }) => ({
        url: 'shared/exchange-session-by-grant',
        method: 'POST',
        body: { grant_id: grantId },
      }),
    }),
  }),
});

export const {
  useCreateSharedSessionGrantMutation,
  useGetSharedSessionGrantsByGranteeUserIdQuery,
  useGetSharedSessionGrantsByGrantorUserIdQuery,
  useRevokeSharedSessionGrantMutation,
  useExchangeSessionByGrantMutation,
} = sharedSessionGrantsApi;
